import {
  DEFAULT_ECONOMIC_LIFE,
  DEFAULT_ECONOMIC_LIFE_MANUFACTURED,
  Improvements,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { CONDITION_TABLE_HEADERS } from '../../../../modules/commentary/consts.ts';
import {
  selectAppliedConditionAdjsDisplay,
  selectConditionAdjs,
} from '../adjustmentSelectors/selectConditionAdj.ts';
import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectDepreciationSettings,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import {
  selectDepreciationRatiosForFeature,
  selectDepreciationRatiosForFeatureDisplay,
} from '../selectDepreciationRatioForFeature.ts';
import { selectDwellingCostNewsDisplay } from '../selectDwellingCostNew.ts';
import { getCommentaryTableHelperText } from '../utils/getCommentaryTableHelperText.ts';

const getConditionRowLabel = (index: number) => {
  if (index === 0) {
    return 'SUB';
  }
  return `CP ${index}`;
};

const getEffectiveAgeString = (
  effectiveAge: number | undefined,
  economicLife: number | undefined,
  defaultEconomicLife: number,
) => `${effectiveAge ?? '-'} / ${economicLife ?? defaultEconomicLife}`;

const calculateDifferenceInDepreciation = (
  subjectDepreciation: number | undefined,
  compDepreciation: number | undefined,
) => {
  if (isUndefined(subjectDepreciation) || isUndefined(compDepreciation)) {
    return undefined;
  }

  return subjectDepreciation - compDepreciation;
};

const getDepreciationDifferenceString = (depreciationDifference: number | string | undefined) => {
  if (isUndefined(depreciationDifference)) {
    return '-';
  }

  if (typeof depreciationDifference === 'string') {
    return depreciationDifference;
  }

  return `${(depreciationDifference * -100).toFixed(2)}%`;
};

const getConditionRow = (
  index: number,
  improvements: Improvements,
  depreciation: string | undefined,
  depreciationDifference: number | string | undefined,
  dwellingCostNewsDisplay: string | undefined,
  adjustment: string | undefined,
  appliedAdjustment: string | undefined,
  depreciationSettings: ReturnType<typeof selectDepreciationSettings>,
) => ({
  label: getConditionRowLabel(index),
  effAgeAndEconomicLife: getEffectiveAgeString(
    improvements.effectiveAge,
    improvements.totalEconomicLife,
    improvements.propertyType === 'manufacturedHome'
      ? (depreciationSettings.defaultEcoLifeManufactured ?? DEFAULT_ECONOMIC_LIFE_MANUFACTURED)
      : (depreciationSettings.defaultEcoLife ?? DEFAULT_ECONOMIC_LIFE),
  ),
  depreciation: depreciation ?? '-',
  depreciationDifference: getDepreciationDifferenceString(depreciationDifference),
  costNew: dwellingCostNewsDisplay ?? '-',
  rawAdjustment: adjustment ?? '-',
  appliedAdjustment: appliedAdjustment ?? '-',
});

const selectConditionCommentaryData = createSelector(
  [
    selectCompSequence,
    selectImprovementsEntities,
    selectDepreciationRatiosForFeatureDisplay,
    selectDepreciationRatiosForFeature,
    selectDwellingCostNewsDisplay,
    selectConditionAdjs,
    selectAppliedConditionAdjsDisplay,
    selectAdjustmentSettings,
    selectDepreciationSettings,
    selectManualAdjustmentsEntities,
  ],
  (
    compSequence,
    improvementsEntities,
    depreciationRatiosDisplay,
    depreciationRatios,
    dwellingCostNewsDisplay,
    conditionAdjs,
    appliedConditionAdjs,
    adjustmentSettings,
    depreciationSettings,
    manualAdjustmentsEntities,
  ) => ({
    helperText: getCommentaryTableHelperText(
      adjustmentSettings,
      'condition',
      undefined,
      manualAdjustmentsEntities,
    ),
    data: ['subject', ...compSequence].map((id, index) => {
      const improvements = improvementsEntities[id];
      const depreciation = depreciationRatiosDisplay?.[id];
      const depreciationDifference =
        id === 'subject'
          ? '-'
          : calculateDifferenceInDepreciation(
              depreciationRatios?.subject,
              depreciationRatios?.[id],
            );

      if (isUndefined(improvements)) {
        throw new Error('error');
      }

      return getConditionRow(
        index,
        improvements,
        depreciation,
        depreciationDifference,
        dwellingCostNewsDisplay?.[id],
        id === 'subject' ? '-' : conditionAdjs?.[id]?.toString(true),
        id === 'subject' ? '-' : appliedConditionAdjs?.[id]?.value,
        depreciationSettings,
      );
    }),
  }),
);

export const selectConditionCommentaryTableDisplay = createSelector(
  [selectConditionCommentaryData],
  ({
    data,
    helperText,
  }): {
    helperText?: string;
    data: [string[], ...string[][]];
  } => ({
    helperText,
    data: [
      CONDITION_TABLE_HEADERS,
      ...data.map(
        ({
          label,
          effAgeAndEconomicLife,
          depreciation,
          depreciationDifference,
          costNew,
          rawAdjustment,
          appliedAdjustment,
        }) => [
          label,
          effAgeAndEconomicLife,
          depreciation,
          depreciationDifference,
          costNew,
          rawAdjustment,
          appliedAdjustment,
        ],
      ),
    ],
  }),
);
